import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { doc, setDoc, serverTimestamp, collection } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  MAX_RECEIPT_SIZE_BYTES,
  RECEIPT_ALLOWED_EXTENSIONS,
  RECEIPT_ALLOWED_MIME_TYPES,
  hasAllowedMimeOrExtension,
} from './uploadPolicy';

export type ReceiptUploadResult = {
  success: boolean;
  expenseId?: string;
  receiptUrl?: string;
  error?: string;
};

type ReceiptUploadOptions = {
  expenseId?: string;
  currency?: string;
  fileName?: string;
  mimeType?: string;
};

/**
 * Upload a receipt image for expense OCR processing
 * The receiptOcr function will automatically process this file and update the expense
 * 
 * @param tripId - The trip ID
 * @param userId - The user ID uploading the receipt
 * @param file - The receipt file (JPEG, PNG, PDF)
 * @param options - Additional upload options
 * @returns The expense ID and receipt URL
 */
export const uploadReceipt = async (
  tripId: string,
  userId: string,
  file: File | Blob,
  options: ReceiptUploadOptions = {}
): Promise<ReceiptUploadResult> => {
  try {
    // Validate file size (8MB max)
    if (file.size > MAX_RECEIPT_SIZE_BYTES) {
      return {
        success: false,
        error: 'File size exceeds 8MB limit',
      };
    }

    const mimeType = (file instanceof File && file.type)
      || options.mimeType
      || (options.fileName?.split('.').pop()?.toLowerCase() === 'pdf' ? 'application/pdf' : 'image/jpeg');

    const extensionFromName = options.fileName?.split('.').pop()?.toLowerCase();
    const receiptFileName = options.fileName || (file instanceof File ? file.name : `receipt_${Date.now()}`);
    if (!hasAllowedMimeOrExtension(mimeType, receiptFileName, RECEIPT_ALLOWED_MIME_TYPES, RECEIPT_ALLOWED_EXTENSIONS)) {
      return {
        success: false,
        error: 'File type not supported. Receipts must be JPG, PNG, or PDF',
      };
    }

    // Generate expense ID if not provided
    const newExpenseId = options.expenseId || doc(collection(db, 'trips', tripId, 'expenses')).id;
    
    // Get file extension
    let extension = 'jpg';

    if (extensionFromName && RECEIPT_ALLOWED_EXTENSIONS.has(extensionFromName)) {
      extension = extensionFromName === 'jpeg' ? 'jpg' : extensionFromName;
    } else if (file instanceof File) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (ext && RECEIPT_ALLOWED_EXTENSIONS.has(ext)) {
        extension = ext === 'jpeg' ? 'jpg' : ext;
      }
    } else if (mimeType === 'application/pdf') {
      extension = 'pdf';
    }

    // Storage path: receipts/{tripId}/{expenseId}.{ext}
    // This matches what the receiptOcr function expects
    const storagePath = `receipts/${tripId}/${newExpenseId}.${extension}`;
    
    // Create storage reference
    const storageRef = ref(storage, storagePath);
    
    // Upload file with metadata for OCR
    const customMetadata: Record<string, string> = {
      uploadedBy: userId,
      currency: options.currency || 'USD',
    };
    if (options.fileName) {
      customMetadata.originalFileName = options.fileName;
    }
    const metadata = {
      contentType: mimeType || (file instanceof File ? file.type : 'image/jpeg'),
      customMetadata,
    };
    
    const snapshot = await uploadBytes(storageRef, file, metadata);
    
    // Get download URL
    const receiptUrl = await getDownloadURL(snapshot.ref);
    
    // Create expense document placeholder (OCR function will update it)
    const expenseRef = doc(db, 'trips', tripId, 'expenses', newExpenseId);
    const expenseData = {
      receiptUrl,
      status: 'pending', // OCR will update this to 'parsed' or 'failed'
      createdBy: userId,
      currency: options.currency || 'USD',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Add defaults so expense is visible while processing
      amount: 0, // Will be updated by OCR
      date: serverTimestamp(), // Default to upload time, OCR may update
      description: 'Scanned receipt', // Placeholder, OCR will extract details
      category: 'other',
    };
    
    await setDoc(expenseRef, expenseData, { merge: true });
    
    return {
      success: true,
      expenseId: newExpenseId,
      receiptUrl,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error('[receiptUpload] Error uploading receipt', { message });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upload receipt',
    };
  }
};

